import * as moment from "moment";
import { IAPITarget, ITypedAPITarget } from "network/NetworkStapler";
import auth from "stores/AuthStore";
import { IAccount, IBuildingsAccount, IBuildingsPayoutInfo, IBuildingsProlongationInfo, ILinkableAccounts, ILinkageResponse, ILinkedAccounts, IPutAccountResponse, ISecuritiesAccount } from "types/account";
import { IBranchResponse, IPlacesResponse } from "types/branch";
/* tslint:disable:max-line-length*/
import { BankCardType, IBankCardMicroPurchaseRequest, IBankCardMicroPurchaseResponse, IBankCardMobilePurchaseRequest, IBankCardMobilePurchaseResponse, IBase64File, ICard, ICardLimits, ICardResult, ICardStateResponse, ICardTanCard, ICreditCardPurchaseRequest, ICreditCardPurchaseResponse, ICreditCardRevolving, IGeoControlActivateResponse, IGeoControlSuspendResponse, IGetCardTanCardsResponse, IGetCreditCardUpgradeResponse, IPostOrderNewCardTanReaderResponse, IPrepaidCardPurchasePutRequest, IPrepaidCardPurchasePutResponse, IPrepaidCardPurchaseRequest, IPrepaidCardPurchaseResponse, IPutCardTanCardsResponse, IRevolvingResponse } from "types/card";
import { IContact, IPostContactResponse } from "types/contact";
import { IRequestDeferralRenewalRequest, IRequestDeferralRequest, IRequestDeferralResponse } from "types/deferralService";
import { IActivateGeorgeIdFromCardTanRequest, IActivateGeorgeIdFromCardTanResponse, IEbvSecAppActivationState, IEbvSecAppPostActivationCodesResponse, IEbvSecappSettings, IEbvSecappSettingsResponse, IGidDevicesResponse, ISIdDevice, ISIdStatusResponse } from "types/ebvSecapp";
import { IAmount, ICardFeeResult } from "types/fee";
import { IGetDailyLimitResponse } from "types/limit";
import { ILoanCalculationResult, ILoanPurchase } from "types/loan";
import { IOffer, IOfferConditionsResponse } from "types/offer";
import { IGetTextResponse, IPlugin, IPluginAgreementList, IPluginPurchaseDetails, IPluginResponse, IPutPluginPurchaseDetailsPayload } from "types/plugins";
import { ProductDetailsResponse } from "types/products";
import { IDataConsents, IEcommerce, IEcommercePutResponse, IIdentification, IIdentificationNew, IJuniorActivationCodeRequest, IJuniorActivationCodeResponse, IJuniorActivationRequest, IJuniorActivationResponse, IJuniorContractRequest, IJuniorContractResponse, IJuniorUsersResponse, IProfile, IPutDataConsentResponse } from "types/profile";
import { IAuthorizationLimits, IAuthorizationLimitsResponse, IGetGidSettingsResponse, IPutAuthorizationLimitsResponse, ISettingsResponse } from "types/settings";
import { ISignInfoResponse } from "types/signing";
import { IStoreHierarchy } from "types/store";
import { ISurveyRequest, ISurveyResponse } from "types/survey";
import { ITopUpResponse } from "types/topUp";
import { IWatchdogResponse } from "types/watchdog";
import { ICountryInfo } from "../types/countryInfo";
import { HouseholdCalculation } from "../types/insurance";
import { IPreferenceList, IPutPreferenceListPayload } from "../types/tradingPreferences";


export function getCards(filterFor?: string): ITypedAPITarget<ICard[]> {
    const queryParameters: any = {
        featureSet: "ALL_NO_EXTERNAL_BALANCES_NO_MISC_NOT_PRODUCED",
    };

    if (filterFor) {
        queryParameters.filterFor = filterFor;
    }

    return {
        url: "/rest/netbanking/my/cards",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.v1+json"
        },
        queryParameters,
        parse: (json) => {
            return (json as any).cards as ICard[];
        }
    };
}

export function getCard(cardId: string): ITypedAPITarget<ICardResult> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.v1+json"
        },
        queryParameters: {
            featureSet: "ALL_NO_EXTERNAL_BALANCES_NO_MISC_NOT_PRODUCED"
        },
        parse: (json) => {
            return json as ICardResult;
        }
    };
}

/**
 * Get fees for
 * @param cardId
 * @param usableFor filter e.g. DEBIT_MOBILE
 */
export function getFeesOfCard(cardId: string, usableFor?: string): ITypedAPITarget<ICardFeeResult> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId + "/fees",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.fees+json"
        },
        queryParameters: usableFor ? {
            usableFor: usableFor
        } : undefined,
        parse: (json: object) => {
            return json as ICardFeeResult;
        }
    };
}

export function getOffers(channelProducts?: string[]): ITypedAPITarget<IOffer[]> {
    return {
        url: `/rest/netbanking/my/offers${channelProducts && channelProducts.length > 0 ? `?channelProducts=${channelProducts?.join(",")}` : ""}`,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.offer+json",
        },
        parse: (json: any) => {
            return json.productOffers as IOffer[];
        }
    };
}

export function getChildOffers(childId: string): ITypedAPITarget<IOffer[]> {
    return {
        url: `/rest/netbanking/my/offers/children?childCustomerId=${childId}`,
        headers: { "Accept": "application/json" },
        parse: (json: any) => {
            return json.productOffers as IOffer[];
        }
    };
}

export function getOfferDetails(offer: IOffer): ITypedAPITarget<any> {
    return getOfferDetailsWithIds(offer.productId, offer.conditionsGroupId);
}

export function getOfferDetailsWithIds(productId: string, conditionsGroupId: string): ITypedAPITarget<any> {
    return {
        url: `/rest/netbanking/my/offers/${productId}/${conditionsGroupId}`,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.offer+json",
        },
        parse: (json: any) => {
            return json;
        }
    };
}

export function getProductConditions(productCode: string, conditionGroupCode: string): ITypedAPITarget<IOfferConditionsResponse> {
    return {
        url: `/my/offers/${productCode}/${conditionGroupCode}`,
        headers: {
            "Accept": "application/vnd.at.spardat.store.myoffers.entities.representation.offer.details+json",
        },
        parse: (json: any) => {
            return json as IOfferConditionsResponse;
        }
    };
}

// Here is an example:
// curl -X GET --location https://call.latest.myoffers.microf.cs.eb.lan.at/moc/rest/my/offers/savings/conditions/G-SMART-K/SMARTAKT01/188 \
//     -H "Authorization: Bearer b90f02ab-c0b2-41f5-bbd8-cded3d49ed16" \
//     -H "Accept: application/vnd.at.spardat.store.myoffers.entities.representation.savings.product+json"
// PROD: https://ecos.erstegroup.com/my/offers/savings/conditions/G-SMART-K/SMARTAKT01/188
// FAT: https://ecos.fat.erstegroup.com/my/offers/savings/conditions/G-SMART-K/SMARTAKT01/188
export function getSavingsOfferConditions(productId: string, channelProductCode: string, institute: number): ITypedAPITarget<any> {
    return {
        url: `/my/offers/savings/conditions/${productId}/${channelProductCode}/${institute}`,
        headers: {
            "Accept": "application/vnd.at.spardat.store.myoffers.entities.representation.savings.product+json",
        },
        parse: (json: any) => {
            return json;
        }
    };
}

export function getCountryInfo(countryIso: string): ITypedAPITarget<ICountryInfo> {
    return {
        url: `/rest/info/countries/${countryIso}`,
        headers: {
            "Accept": "application/json"
        },
        parse: (json: any) => {
            return json;
        }
    };
}

export function createAccidentInsurance(options: {
    emailContactId: string;
    addressContactId: string;
    mobilePhoneContactId: string;
    settlementAccountIban: string;
}): ITypedAPITarget<{ url: string }> {
    return {
        url: "/rest/products/external/insurances/accident",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.products.external.insurances.accident+json",
            "Content-Type": "application/vnd.at.spardat.rest.products.external.insurances.accident+json"
        },
        method: "POST",
        body: options,
        parse: (json: any) => {
            return json;
        }
    };
}

export function createPensionInsurance(options: {
    emailContactId: string;
    addressContactId: string;
    mobilePhoneContactId: string;
    settlementAccountIban: string;
}): ITypedAPITarget<{ url: string }> {
    return {
        url: "/rest/products/external/insurances/pension",
        headers: {
            "Accept": "application/vnd.presto.latest+json",
            "Content-Type": "application/vnd.presto.latest+json"
        },
        method: "POST",
        body: options,
        parse: (json: any) => {
            return json;
        }
    };
}

export function createLifeInsurance(options: {
    emailContactId: string;
    addressContactId: string;
    mobilePhoneContactId: string;
    settlementAccountIban: string;
}): ITypedAPITarget<{ url: string }> {
    return {
        url: "/rest/products/external/insurances/termlife",
        headers: {
            "Accept": "application/vnd.presto.latest+json",
            "Content-Type": "application/vnd.presto.latest+json"
        },
        method: "POST",
        body: options,
        parse: (json: any) => {
            return json;
        }
    };
}


export function createPensionFunds(
    contractId: string,
    options: {
        emailContactId: string;
        addressContactId: string;
        mobilePhoneContactId: string;
    },
): ITypedAPITarget<{ url: string }> {
    return {
        url: `/rest/products/external/insurances/${contractId}/pension-funds`,
        headers: {
            "Accept": "application/vnd.presto.latest+json",
            "Content-Type": "application/vnd.presto.latest+json"
        },
        method: "POST",
        body: options,
        parse: (json: any) => {
            return json;
        }
    };
}

export function getAccounts(usableFor?: string, filterFor?: string, filterProductId?: string): ITypedAPITarget<IAccount[]> {
    const queryParameters: any = {};

    if (usableFor) {
        queryParameters.usableFor = usableFor;
    }
    if (filterFor) {
        queryParameters.filterFor = filterFor;
    }
    if (filterProductId) {
        queryParameters.filterProductId = filterProductId;
    }

    return {
        url: "/rest/netbanking/my/accounts",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.account.v2+json",
        },
        parse: (json: any) => {
            return json.accounts as IAccount[];
        },
        queryParameters: Object.keys(queryParameters).length > 0 ? queryParameters : undefined
    };
}

export function getAccount(accountId: string): ITypedAPITarget<IAccount> {
    return {
        url: `/rest/netbanking/my/accounts/${accountId}`,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.account.v2+json",
        },
        parse: (json: any) => {
            return json.account as IAccount;
        },
    };
}

export function putAccount(accountId: string, account: IAccount): ITypedAPITarget<IPutAccountResponse> {
    return {
        url: `/rest/netbanking/my/accounts/${accountId}`,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.account.v2+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.account.v2+json"
        },
        method: "PUT",
        parse: (json: any) => {
            return json as IPutAccountResponse;
        },
        body: account
    };
}

export function getSecuritiesAccounts(): ITypedAPITarget<ISecuritiesAccount[]> {
    return {
        url: "/rest/netbanking/my/securities",
        headers: {
            "Accept": "application/json",
        },
        parse: (json: any) => {
            return json.securitiesAccounts as ISecuritiesAccount[];
        },
    };
}


export function getBuildingsAccounts(): ITypedAPITarget<IBuildingsAccount[]> {
    return {
        url: "/rest/netbanking/my/contracts/buildings",
        headers: {
            "Accept": "application/json",
        },
        parse: (json: any) => {
            return json.buildings as IBuildingsAccount[];
        },
    };
}

export function getBuildingsAccountById(id: string): ITypedAPITarget<IBuildingsAccount> {
    return {
        url: `/rest/netbanking/my/contracts/buildings/${id}`,
        headers: {
            "Accept": "application/json",
        },
        parse: (json: any) => {
            return json as IBuildingsAccount;
        },
    };
}

export function getBuildingsAccountPayoutInfoById(id: string): ITypedAPITarget<IBuildingsPayoutInfo> {
    return {
        url: `/rest/netbanking/my/contracts/buildings/${id}/payout/info`,
        headers: {
            "Accept": "application/json",
        },
        parse: (json: any) => {
            return json as IBuildingsPayoutInfo;
        },
    };
}

export function getBuildingsProlongationInfo(id: string): ITypedAPITarget<IBuildingsProlongationInfo> {
    return {
        url: `/rest/netbanking/my/contracts/buildings/${id}/prolong/info`,
        headers: {
            "Accept": "application/json",
        },
        parse: (json: any) => {
            return json as IBuildingsProlongationInfo;
        },
    };
}

export function postBuildingsProlongationPdf(id: string): IAPITarget {
    return {
        url: `/rest/netbanking/my/contracts/buildings/${id}/prolong/pdf`,
        headers: {
            "Accept": "application/octet-stream",
        },
        method: "POST",
    };
}

export function postBuildingsProlongation(id: string): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: `/rest/netbanking/my/contracts/buildings/${id}/prolong`,
        headers: {
            "Accept": "application/json",
        },
        method: "POST",
        parse: (json: any) => {
            return json as ISignInfoResponse;
        },
    };
}

export function postBuildingsPayout(id: string, payoutAccountId: string): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: `/rest/netbanking/my/contracts/buildings/${id}/payout`,
        headers: {
            "Accept": "application/json",
        },
        body: {
            payoutAccountId
        },
        method: "POST",
        parse: (json: any) => {
            return json as ISignInfoResponse;
        },
    };
}

export function postBuildingsPayoutPdf(id: string, payoutAccountId: string): IAPITarget {
    return {
        url: `/rest/netbanking/my/contracts/buildings/${id}/payout/pdf`,
        headers: {
            "Accept": "application/octet-stream",
        },
        body: {
            payoutAccountId
        },
        method: "POST",
    };
}

export function getLinkedAccounts(cardId: string): ITypedAPITarget<ILinkedAccounts> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId + "/accounts",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.account+json",
        },
        parse: (json: any) => {
            return json as ILinkedAccounts;
        },
    };
}

export function getLinkableAccounts(cardId: string): ITypedAPITarget<ILinkableAccounts> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId + "/accounts/linkable",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.account+json",
        },
        parse: (json: any) => {
            return json as ILinkableAccounts;
        },
    };
}

export function putAccoutsLinkage(cardId: string, linkAccountIDs: string[], unlinkAccountIDs: string[]): ITypedAPITarget<ILinkageResponse> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId + "/accounts/linkage",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.account+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.card.account+json"
        },
        method: "PUT",
        body: {
            linkAccounts: linkAccountIDs.map(accountId => ({ accountId })),
            unlinkAccounts: unlinkAccountIDs.map(accountId => ({ accountId }))
        },
        parse: (json: any) => {
            return json as ILinkageResponse;
        },
    };
}


export function getContacts(filterFor?: string): ITypedAPITarget<IContact[]> {
    const filter = filterFor ? "?filterFor=" + filterFor : "";
    return {
        url: "/rest/netbanking/my/contacts" + filter,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.contact+json",
        },
        parse: (json: any) => {
            return json.contacts as IContact[];
        }
    };
}

export function getProfile(): ITypedAPITarget<IProfile> {
    return {
        url: "/rest/netbanking/my/profile",
        headers: {
            // Change due to the request of the Erste Bank
            // "Accept": "application/vnd.at.spardat.rest.netbanking.model.profile+json",
            "Accept": "application/json",
        },
        parse: (json: any) => {
            return json as IProfile;
        }
    };
}

export function getEcommerce(): ITypedAPITarget<IEcommerce> {
    return {
        url: "/rest/netbanking/my/profile/at/ecommerce",
        headers: {
            "Accept": "application/vnd.at.sitsolutions.services.pms.profile.representation.ecommerce+json",
        },
        parse: (json: any) => {
            return json as IEcommerce;
        }
    };
}

export function putEcommerce(options: IEcommerce): ITypedAPITarget<IEcommercePutResponse> {
    return {
        url: "/rest/netbanking/my/profile/at/ecommerce",
        headers: {
            "Content-Type": "application/vnd.at.sitsolutions.services.pms.profile.representation.ecommerce+json",
            "Accept": "application/vnd.at.sitsolutions.services.pms.profile.representation.ecommerce+json",
        },
        method: "PUT",
        body: options,
        parse: (json: any) => {
            return json as IEcommercePutResponse;
        }
    };
}

export function putAlias(alias: string | null): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: "/ebv-user/rest/alias-maintenance",
        headers: {
            "Content-Type": "application/vnd.at.sitsolutions.services.ebv.user.representation.alias.reservation.request+json"
        },
        method: "PUT",
        body: {
            alias
        },
        parse: (json: any) => {
            return json as ISignInfoResponse;
        }
    };
}

export function deleteAlias(): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: "/ebv-user/rest/alias-maintenance",
        headers: {
            "Content-Type": "application/vnd.at.sitsolutions.services.ebv.user.representation.alias.maintenance.response+json"
        },
        method: "DELETE",
        parse: (json: any) => {
            return json as ISignInfoResponse;
        }
    };
}

/**
 * Calculates loan parameters depending on user entries
 *
 * @param amount
 * @param durationInMonths
 * @param withInsurance
 */
export function calculateLoan(amount: IAmount, durationInMonths: number, withInsurance: boolean): ITypedAPITarget<ILoanCalculationResult> {
    return {
        url: "/rest/netbanking/my/products/consumerloan/preapproved/calculation",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.products.consumerloan.preapproved.calculation+json",
            "Content-Type": "application/vnd.at.spardat.rest.products.consumerloan.preapproved.calculation+json"
        },
        method: "POST",
        body: {
            input: {
                loanAmount: amount,
                term: durationInMonths,
                insurance: withInsurance ? "WITH_INSURANCE" : "WITHOUT_INSURANCE"
            }
        },
        parse: (json: any) => {
            return json as ILoanCalculationResult;
        }
    };
}

export function purchaseLoan(
    amount: IAmount,
    durationInMonths: number,
    withInsurance: boolean,
    account: IAccount,
    emailId: string): ITypedAPITarget<ILoanPurchase> {
    return {
        url: "/rest/netbanking/my/products/consumerloan/preapproved/purchase",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.products.consumerloan.preapproved.purchase+json",
            "Content-Type": "application/vnd.at.spardat.rest.products.consumerloan.preapproved.purchase+json"
        },
        method: "POST",
        body: {
            loanAmount: amount,
            term: durationInMonths,
            insurance: withInsurance ? "WITH_INSURANCE" : "WITHOUT_INSURANCE",
            disbursementAccountId: account.id,
            emailContactId: emailId
        },
        parse: (json: any) => {
            return json as ILoanPurchase;
        }
    };
}

export function purchaseCreditCard(body: ICreditCardPurchaseRequest): ITypedAPITarget<ICreditCardPurchaseResponse> {
    return {
        url: "/rest/netbanking/my/cardproducts/at/screditcard",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.cardproduct.screditcard+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.cardproduct.screditcard+json"
        },
        method: "POST",
        body,
        parse: (json: any) => json as ICreditCardPurchaseResponse
    };
}

export function purchasePrepaidCardPost(body: IPrepaidCardPurchaseRequest): ITypedAPITarget<IPrepaidCardPurchaseResponse> {
    return {
        url: "/rest/netbanking/my/cardproducts/at/sprepaidcard",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.cardproduct+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.cardproduct+json"
        },
        method: "POST",
        body,
        parse: (json: any) => json as IPrepaidCardPurchaseResponse
    };
}

export function purchasePrepaidCardPut(orderId: string, body: IPrepaidCardPurchasePutRequest): ITypedAPITarget<IPrepaidCardPurchasePutResponse> {
    return {
        url: "/rest/netbanking/my/cardproducts/at/sprepaidcard/" + orderId,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.cardproduct+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.cardproduct+json"
        },
        method: "PUT",
        body,
        parse: (json: any) => json as IPrepaidCardPurchasePutResponse
    };
}

export function downloadPrepaidPdfGet(cardId: string): ITypedAPITarget<IBase64File> {
    return {
        url: "/rest/netbanking/my/cardproducts/at/sprepaidcard/" + cardId + "/carrier",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.cardproduct+json"
        },
        method: "GET",
        parse: json => json as IBase64File
    };
}

export function purchaseBankCardMicro(type: BankCardType, body: IBankCardMicroPurchaseRequest): ITypedAPITarget<IBankCardMicroPurchaseResponse> | undefined {
    let endpoint = "";
    switch (type) {
        case BankCardType.MICRO:
            endpoint = "microchip";
            break;
        case BankCardType.STICKER:
            endpoint = "microsticker";
            break;
        default:
            return;
    }

    const acceptType = `application/vnd.at.spardat.rest.netbanking.model.cardproduct.${endpoint}+json`;

    return {
        url: "/rest/netbanking/my/cardproducts/at/" + endpoint,
        headers: {
            "Accept": acceptType,
            "Content-Type": acceptType
        },
        method: "POST",
        body,
        parse: json => json as IBankCardMicroPurchaseResponse
    };
}

export function purchaseBankCardMobile(body: IBankCardMobilePurchaseRequest): ITypedAPITarget<IBankCardMobilePurchaseResponse> {
    return {
        url: "/rest/netbanking/my/cardproducts/at/sdebitmobile",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.cardproduct.sdebitmobilecard+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.cardproduct.sdebitmobilecard+json"
        },
        method: "POST",
        body,
        parse: json => json as IBankCardMobilePurchaseResponse
    };
}

export function postEmail(email: string, type: "PRIVATE" | "COMPANY", mainContact: boolean): ITypedAPITarget<IPostContactResponse> {
    return {
        url: "/rest/netbanking/my/contacts",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.contact+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.contact+json"
        },
        method: "POST",
        body: {
            type: "EMAIL",
            email: {
                email,
                type,
                flags: mainContact ? ["mainContact"] : undefined
            }
        },
        parse: (json: any) => json as IPostContactResponse
    };
}

export function postPhone(
    countryCallingCode: string,
    areaCallingCode: string,
    phoneNumber: string,
    weekdays: boolean,
    saturday: boolean,
    sundayHolidays: boolean,
    type: "PRIVATE" | "COMPANY",
    mobile: boolean,
    mainContact: boolean): ITypedAPITarget<IPostContactResponse> {
    const reachableDays = [];
    if (weekdays) {
        reachableDays.push({ reachableDaysValue: "WEEKDAYS" });
    }
    if (saturday) {
        reachableDays.push({ reachableDaysValue: "SATURDAY" });
    }
    if (sundayHolidays) {
        reachableDays.push({ reachableDaysValue: "SUNDAY_AND_HOLIDAYS" });
    }

    const flags = [];
    if (mainContact) {
        flags.push("mainContact");
    }
    if (mobile) {
        flags.push("mobile");
    }

    return {
        url: "/rest/netbanking/my/contacts",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.contact+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.contact+json"
        },
        method: "POST",
        body: {
            type: "PHONE",
            phone: {
                countryCallingCode,
                areaCallingCode,
                phoneNumber,
                reachableFrom: "00:00",    // HH:mm (e.g. 08:00)
                reachableUntil: "00:00",   // HH:mm (e.g. 16:00)
                reachableDays,
                type,
                flags
            }
        },
        parse: (json: any) => json as IPostContactResponse
    };
}

export function postAddress(
    street: string,
    streetNumber: string,
    zipCode: string,
    city: string,
    country: string,
    mainContact: boolean
): ITypedAPITarget<IPostContactResponse> {
    const flags = [];
    if (mainContact) {
        flags.push("mainContact");
    }

    return {
        url: "/rest/netbanking/my/contacts",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.contact+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.contact+json"
        },
        method: "POST",
        body: {
            type: "ADDRESS",
            address: {
                street,
                streetNumber,
                zipCode,
                city,
                country,
                flags
            }
        },
        parse: (json: any) => json as IPostContactResponse
    };
}

export function putAddress(
    address: IContact,
    street: string,
    streetNumber: string,
    zipCode: string,
    city: string,
    country: string,
    mainContact: boolean
): ITypedAPITarget<IPostContactResponse> {
    let flags = address.flags;
    if (flags) {
        if (mainContact && !flags.includes("mainContact")) {
            flags = [...flags, "mainContact"];
        } else if (!mainContact && flags.includes("mainContact")) {
            flags = flags.filter(flag => flag !== "mainContact");
        }
    }

    return {
        url: "/rest/netbanking/my/contacts/" + address.id,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.contact+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.contact+json"
        },
        method: "PUT",
        body: {
            ...address,
            flags,
            address: {
                ...address.address,
                street,
                streetNumber,
                zipCode,
                city,
                country,
            }
        },
        parse: (json: any) => json as IPostContactResponse
    };
}

export function postWatchDog(cardId: string, phoneId: string): ITypedAPITarget<IWatchdogResponse> {
    return {
        url: "/watchdogs/rest/notifications/triggers",
        headers: {
            "Content-Type": "application/vnd.at.sitsolutions.services.watchdogs.model.notification+json"
        },
        method: "POST",
        body: {
            active: true,
            origin: "WATCH_DOG",
            product: "CREDIT_CARD",
            type: "CC_KUNDENWUNSCH_0020",
            deliveryDetails: [
                {
                    deliveryType: "sms",
                    active: true,
                    contactId: phoneId
                }
            ],
            triggerCriterias: [
                {
                    identifier: "0020",
                    type: "boolean",
                    mandatory: true,
                    active: true
                },
                {
                    identifier: "mobileAppContext",
                    type: "string",
                    mandatory: true,
                    text: "MYCARDS"
                },
                {
                    identifier: "cardId",
                    type: "string",
                    mandatory: true,
                    text: cardId
                }
            ],
            mobileApp: "MYCARDS",
            activeChannels: 0
        },
        parse: (json: any) => json as IWatchdogResponse
    };
}

export function getCreditCardLimits(): ITypedAPITarget<ICardLimits> {
    return {
        url: "/rest/netbanking/my/cardproducts/at/screditcard/limits",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.cardproduct.screditcard.limits+json"
        },
        parse: (json) => {
            return json as ICardLimits;
        }
    };
}

export function getProfileIdentification(): ITypedAPITarget<IIdentification> {
    return {
        url: "/rest/netbanking/my/profile/at/identification",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.profile.identification+json"
        },
        parse: (json) => {
            return json as IIdentification;
        }
    };
}

export function postTopUp(card: ICard, payerAccount: IAccount, amount: IAmount): ITypedAPITarget<ITopUpResponse> {
    return {
        method: "POST",
        url: `/rest/netbanking/my/cards/at/prepaid/${card.id}/topup`,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.prepaidcard.topup+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.prepaidcard.topup+json"
        },
        body: {
            "loadAmount": amount,
            "payerAccount": payerAccount.id
        },
        parse: (json) => {
            return json as ITopUpResponse;
        }
    };
}

export function getProfileIdentificationNew(): ITypedAPITarget<IIdentificationNew> {
    return {
        url: "/rest/netbanking/my/profile/identification",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.identification+json"
        },
        parse: (json) => {
            return json as IIdentificationNew;
        }
    };
}

export function deleteRevolving(cardId: string): ITypedAPITarget<IRevolvingResponse> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId + "/revolving",
        method: "DELETE",
        parse: (json) => {
            return json as IRevolvingResponse;
        }
    };
}

export function postRevolving(cardId: string, body: ICreditCardRevolving): ITypedAPITarget<IRevolvingResponse> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId + "/revolving",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.account+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.card.account+json"
        },
        method: "POST",
        body,
        parse: (json) => {
            return json as any;
        }
    };
}

export function putRevolving(cardId: string, body: ICreditCardRevolving): ITypedAPITarget<IRevolvingResponse> {
    const ret = postRevolving(cardId, body);
    ret.method = "PUT";
    return ret;
}

export function getRevolvingVvi(cardId: string, amount: number, percentage: number): ITypedAPITarget<IBase64File> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId + "/revolving/docs/vvi",
        queryParameters: {
            amount,
            percentage
        },
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.account+json"
        },
        method: "GET",
        parse: json => json as IBase64File
    };
}

export function getRevolvingContract(cardId: string, amount: number, percentage: number): ITypedAPITarget<IBase64File> {
    return {
        url: "rest/netbanking/my/cards/" + cardId + "/revolving/docs/credit-contract/",
        queryParameters: {
            amount,
            percentage
        },
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.account+json"
        },
        method: "GET",
        parse: json => json as IBase64File
    };
}

/**
 * Suspends geo control until a certain date
 */
export function putGeoControl(cardId: string, suspendedUntil: Date): ITypedAPITarget<IGeoControlSuspendResponse> {
    const queryParameters = { suspendedUntil: moment(suspendedUntil).format("YYYY-MM-DD") };
    return {
        url: "rest/netbanking/my/cards/" + cardId + "/geo-control",
        queryParameters,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.geocontrol+json"
        },
        method: "PUT",
        parse: json => json as IGeoControlSuspendResponse
    };
}

/**
 * Activates geo control
 */
export function postGeoControl(cardId: string): ITypedAPITarget<IGeoControlActivateResponse> {
    return {
        url: "rest/netbanking/my/cards/" + cardId + "/geo-control",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.geocontrol+json"
        },
        method: "POST",
        parse: json => json as IGeoControlActivateResponse
    };
}

export function putStates(cardId: string, body: {
    action: string,
    closeReason?: string,
    payeeAccount?: string
}): ITypedAPITarget<ICardStateResponse> {
    return {
        url: "rest/netbanking/my/cards/" + cardId + "/states",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.state.v1+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.card.state.v1+json"
        },
        method: "PUT",
        body,
        parse: json => json as ICardStateResponse
    };
}

export function postActivate(cardId: string): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: "rest/netbanking/my/cards/" + cardId + "/at/activate",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.card.microcard.activation+json"
        },
        method: "POST",
        parse: json => json as ISignInfoResponse
    };
}

export function getUpgradeDetails(cardId: string): ITypedAPITarget<IGetCreditCardUpgradeResponse> {
    return {
        url: "rest/netbanking/my/cards/" + cardId + "/at/upgrade",
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: json => json as IGetCreditCardUpgradeResponse
    };
}

export function postUpgrade(cardId: string, addressId: string, emailId: string): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: "rest/netbanking/my/cards/" + cardId + "/at/upgrade",
        headers: {
            "Accept": "application/json"
        },
        body: {
            deliveryAddress: addressId,
            email: emailId
        },
        method: "POST",
        parse: json => json as ISignInfoResponse
    };
}

export function getSettings(): ITypedAPITarget<ISettingsResponse> {
    return {
        url: "rest/netbanking/my/settings",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.settings+json"
        },
        method: "GET",
        parse: json => json as ISettingsResponse
    };
}

export function postEbvSecappSettings(body: IEbvSecappSettings): ITypedAPITarget<IEbvSecappSettingsResponse> {
    return {
        url: "ebv-secapp/rest/settings",
        body,
        headers: {
            "Accept": "application/vnd.at.sitsolutions.services.ebv.secapp.representation.settings.view.george.nested.with.sign.info.v2+json",
            "Content-Type": "application/vnd.at.sitsolutions.services.ebv.secapp.representation.settings.view.george.request.v2+json",
            "Client-Accept-Language": auth.locale
        },
        method: "POST",
        parse: json => json as IEbvSecappSettingsResponse
    };
}

export function putEbvSecappActivationState(): ITypedAPITarget<IEbvSecAppActivationState> {
    return {
        url: "ebv-secapp/rest/activationstate",
        headers: {
            "Accept": "application/vnd.at.sitsolutions.services.ebv.secapp.representation.activationstate.view.george.response.v2+json"
        },
        method: "PUT",
        parse: json => json as IEbvSecAppActivationState
    };
}

export function postActivationCodes(): ITypedAPITarget<IEbvSecAppPostActivationCodesResponse> {
    return {
        url: "ebv-secapp/rest/activationcodes/v3",
        headers: {
            "Content-Type": "application/vnd.at.sitsolutions.services.ebv.secapp.representation.activation.code.request.v2+json",
            "Accept": "application/vnd.at.sitsolutions.services.ebv.secapp.representation.activation.code.response.v2+json"
        },
        method: "POST",
        body: { codeType: "APP" },
        parse: json => json as IEbvSecAppPostActivationCodesResponse
    };
}

export function deleteEbvSecappSettings(): IAPITarget {
    return {
        url: "ebv-secapp/rest/settings",
        method: "DELETE"
    };
}

export function getSIdSettingsStatus(): ITypedAPITarget<ISIdStatusResponse> {
    return {
        url: "ebv-secapp/rest/settings",
        headers: {
            "Accept": "application/vnd.at.sitsolutions.services.ebv.secapp.representation.settings.read.view.george.response.v2+json"
        },
        method: "GET",
        parse: json => json as ISIdStatusResponse
    };
}

export function getAuthorizationLimits(): ITypedAPITarget<IAuthorizationLimitsResponse> {
    return {
        url: "rest/netbanking/my/authorizationLimits",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.limits.authorization+json",
        },
        method: "GET",
        parse: json => json as IAuthorizationLimitsResponse
    };
}

export function putAuthorizationLimits(limits: IAuthorizationLimits): ITypedAPITarget<IPutAuthorizationLimitsResponse> {
    return {
        url: "rest/netbanking/my/authorizationLimits/" + limits.id,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.limits.authorization+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.limits.authorization+json"
        },
        body: limits,
        method: "PUT",
        parse: json => json as IPutAuthorizationLimitsResponse
    };
}

export function getGidSettings(): ITypedAPITarget<IGetGidSettingsResponse> {
    return {
        url: `/rest/my/software-token/settings`,
        headers: {
            "Accept": "application/json",
        },
        method: "GET",
        parse: json => json as IGetGidSettingsResponse
    };
}

export function getDailyLimit(): ITypedAPITarget<IGetDailyLimitResponse> {
    return {
        url: `/rest/my/software-token/daily-limit`,
        headers: {
            "Accept": "application/json",
        },
        method: "GET",
        parse: json => json as IGetDailyLimitResponse
    };
}

export function putGidSettings(limit: IAmount): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: `/rest/my/software-token/settings`,
        headers: {
            "Accept": "application/json",
        },
        body: {
            gidAppDailyLimit: limit
        },
        method: "PUT",
        parse: json => json as ISignInfoResponse
    };
}

export function putDailyLimit(limit: IAmount, status: string): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: `/rest/my/software-token/daily-limit`,
        headers: {
            "Accept": "application/json",
        },
        body: {
            gidAppDailyLimit: limit,
            status: status
        },
        method: "PUT",
        parse: json => json as ISignInfoResponse
    };
}

export function getSIdSettingsDevices(): ITypedAPITarget<ISIdDevice[]> {
    return {
        url: "ebv-secapp/rest/devices",
        headers: {
            "Accept": "application/vnd.at.sitsolutions.services.ebv.secapp.representation.device.view.george.collection+json"
        },
        method: "GET",
        parse: json => json as ISIdDevice[]
    };
}

export function deleteSIdSettingsDevice(deviceId: string): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: "ebv-secapp/rest/devices/" + deviceId,
        headers: {
            "Accept": "application/vnd.at.sitsolutions.services.ebv.secapp.representation.delete.device.george.nested.with.sign.info+json"
        },
        method: "DELETE",
        parse: json => json as ISignInfoResponse
    };
}

export function getGidSettingsDevices(): ITypedAPITarget<IGidDevicesResponse> {
    return {
        url: "/ebv-gapp/rest/software-token/devices",
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: json => json as IGidDevicesResponse
    };
}

export function deleteGidSettingsDevice(deviceId: string): IAPITarget {
    return {
        url: `/ebv-gapp/rest/software-token/devices/${deviceId}`,
        headers: {
            "Accept": "application/json"
        },
        method: "DELETE",
        // parse: json => json as ISignInfoResponse
    };
}

export function getCardTanDebitCards(): ITypedAPITarget<IGetCardTanCardsResponse> {
    return {
        url: "/rest/netbanking/my/settings/at/cardtan/cards?size=100000",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.settings.cardtan.cards+json"
        },
        method: "GET",
        parse: json => json as IGetCardTanCardsResponse
    };
}

export function postOrderNewCardTanDevice(payload: { "numberOfReaders": number, "unitPrice": IAmount, "emailContactId": string, "recipientAddressId": string }): ITypedAPITarget<IPostOrderNewCardTanReaderResponse> {
    return {
        url: "rest/netbanking/my/settings/at/cardtan/reader",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.settings.cardtan.reader+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.settings.cardtan.reader+json"
        },
        body: payload,
        method: "POST",
        parse: json => json as IPostOrderNewCardTanReaderResponse
    };
}

export function putCardTanDebitCards(payload: { cardTanCards: ICardTanCard[] }): ITypedAPITarget<IPutCardTanCardsResponse> {
    return {
        url: "/rest/netbanking/my/settings/at/cardtan/cards",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.settings.cardtan.cards+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.settings.cardtan.cards+json"
        },
        body: payload,
        method: "PUT",
        parse: json => json as IPutCardTanCardsResponse
    };
}

export function putCardTanPin(pinEncrypted: string): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: "/rest/netbanking/my/settings/at/cardtan/pin",
        headers: {
            // "Accept": "application/vnd.at.spardat.rest.netbanking.model.settings.cardtan.cards+json",
        },
        body: {
            oldNewCardTanPin: pinEncrypted
        },
        method: "PUT",
        parse: json => json as ISignInfoResponse
    };
}

export function getAccountImage(accountId: string): IAPITarget {
    return {
        url: `/proxy/g/api/my/accounts/${accountId}/images/image?variant=mobile`,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.settings.cardtan.cards+json"
        },
        method: "GET",
    };
}

export function tokenExchange(clientId?: string, isBusiness?: boolean): ITypedAPITarget<{ access_token: string }> {
    const url = isBusiness ? `pxy/exchange?targetClientId=${clientId}` : `proxy/exchange?targetClientId=${clientId ?? "georgeclient"}`;
    return {
        url,
        headers: {
            "Accept": "*.*"
        },
        method: "GET",
        parse: json => json as { access_token: string }
    };
}

export function getPlugins(): ITypedAPITarget<IPluginResponse> {
    return {
        url: "rest/netbanking/my/plugins",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.plugin+json"
        },
        method: "GET",
        parse: json => json as IPluginResponse
    };
}

export function putPlugin(plugin: IPlugin & { emailId?: string }): ITypedAPITarget<IPluginResponse> {
    return {
        url: `rest/netbanking/my/plugins/${plugin.productCode}`,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.netbanking.model.plugin+json",
            "Content-Type": "application/vnd.at.spardat.rest.netbanking.model.plugin+json"
        },
        body: plugin,
        method: "PUT",
        parse: json => json as IPluginResponse
    };
}

export function getPluginPurchaseDetails(plugin: IPlugin): ITypedAPITarget<IPluginPurchaseDetails> {
    return {
        url: `rest/netbanking/my/plugins/${plugin.productCode}/purchase`,
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
        },
        method: "POST",
        body: {
            status: "DRAFT"
        },
        parse: json => json as IPluginPurchaseDetails
    };
}

export function putPluginPurchaseDetails(plugin: IPlugin, purchaseId: string, body: IPutPluginPurchaseDetailsPayload): ITypedAPITarget<IPluginPurchaseDetails> {
    return {
        url: `rest/netbanking/my/plugins/${plugin.productCode}/purchase/${purchaseId}`,
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
        },
        method: "PUT",
        body,
        parse: json => json as IPluginPurchaseDetails
    };
}

export function getPluginAgreementList(plugin: IPlugin): ITypedAPITarget<IPluginAgreementList> {
    return {
        url: `rest/netbanking/my/plugins/${plugin.productCode}/agreement-list`,
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
        },
        method: "GET",
        parse: json => json as IPluginAgreementList
    };
}

export function getText(textId: string): ITypedAPITarget<IGetTextResponse> {
    return {
        url: `rest/info/texts/${textId}`,
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
        },
        method: "GET",
        parse: json => json as IGetTextResponse
    };
}

export function getPluginDetails(): ITypedAPITarget<any> {
    return {
        url: "proxy/gebof/api/public/plugins?retinaResolution=true&size=1000",
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: json => json as any
    };
}

export function getProductDetails(): ITypedAPITarget<ProductDetailsResponse> {
    return {
        url: "proxy/gebof/api/public/products?retinaResolution=false&size=1000",
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: json => json as ProductDetailsResponse
    };
}

export function getStoreHierarchy(): ITypedAPITarget<IStoreHierarchy> {
    return {
        url: "proxy/gebof/api/public/store/hierarchies",
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: json => json as any
    };
}

export function postRequestReferral(body: IRequestDeferralRequest | IRequestDeferralRenewalRequest): ITypedAPITarget<IRequestDeferralResponse> {
    return {
        url: "/rest/netbanking/my/lux",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
        },
        method: "POST",
        body,
        parse: json => json as IRequestDeferralResponse
    };
}

export function postSurvey(body: ISurveyRequest): ITypedAPITarget<ISurveyResponse> {
    return {
        url: "/rest/netbanking/my/survey",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
        },
        method: "POST",
        body,
        parse: json => json as ISurveyResponse
    };
}

export function activateGeorgeIdFromCardTan(body: IActivateGeorgeIdFromCardTanRequest): ITypedAPITarget<IActivateGeorgeIdFromCardTanResponse> {
    return {
        url: "/ebv-signmode-change/rest/signmodes/gidentity/switchfromcardtan/withqrcode",
        headers: {
            "Content-Type": "application/vnd.at.sitsolutions.services.ebv.signmode.change.representation.g.app.daily.limit.request+json",
            "Accept": "application/vnd.at.sitsolutions.services.ebv.signmode.change.representation.gid.activation.response+json"
        },
        method: "POST",
        body,
        parse: json => json as IActivateGeorgeIdFromCardTanResponse
    };
}

export function getBranch(branchId: string): ITypedAPITarget<IBranchResponse> {
    return {
        // Changed to haystack API which is the correct one (verified with Mladen) url: "/rest/info/branches/" + branchId,
        url: "/haystack/rest/info/branches/" + branchId,
        headers: {
            "Accept": "application/vnd.at.spardat.rest.info.model.search+json",
        },
        parse: json => json as IBranchResponse
    };
}

export function getBranches(topicId: string, query?: string): ITypedAPITarget<IPlacesResponse> {
    return {
        url: `/haystack/rest/places?types=branch&appointmentTopicId=${topicId}${query ? `&q=${query}` : ""}`,
        headers: {
            "Accept": "application/json"
        },
        parse: (json: any) => {
            return json as IPlacesResponse;
        }
    };
}


export function getMarketingInformation(): ITypedAPITarget<IDataConsents> {
    return {
        url: "/rest/netbanking/my/profile/data-consents",
        headers: {
            "Accept": "application/vnd.at.sitsolutions.services.pms.profile.representation.data.consent+json"
        },
        parse: (json: any) => {
            return json as IDataConsents;
        }
    };
}

export function putDataConsent(payload: IDataConsents): ITypedAPITarget<IPutDataConsentResponse> {
    return {
        url: "/rest/netbanking/my/profile/data-consents",
        headers: {
            "Content-Type": "application/vnd.at.sitsolutions.services.pms.profile.representation.data.consent+json"
        },
        method: "PUT",
        body: payload,
        parse: (json: any) => {
            return json as IPutDataConsentResponse;
        }
    };
}

export function postHouseholdInsuranceCalculationData(payload: {
    region: number;
    livingSpace: number;
}): ITypedAPITarget<HouseholdCalculation> {
    return {
        url: "insurance/rest/calculate/household",
        headers: {
            "Content-Type": "application/vnd.at.sitsolutions.services.pms.insurance.representation.calculate.household.request+json",
        },
        method: "POST",
        body: payload,
        parse: (json: any) => {
            return json as HouseholdCalculation;
        }
    };
}

export function getJuniorUsers(): ITypedAPITarget<IJuniorUsersResponse> {
    return {
        url: "/rest/netbanking/my/profile/junior-users?onboarding=true",
        headers: {
            "Accept": "application/json",
        },
        parse: (json: any) => {
            return json as IJuniorUsersResponse;
        }
    };
}

export function activateJunior(body: IJuniorActivationRequest): ITypedAPITarget<IJuniorActivationResponse> {
    return {
        url: "/ebv-onboarding/rest/junior-activation",
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
        },
        method: "POST",
        body,
        parse: (json: any) => json as IJuniorActivationResponse
    };
}

export function createJuniorContract(body: IJuniorContractRequest): ITypedAPITarget<IJuniorContractResponse> {
    return {
        url: "/ebv-onboarding/rest/junior-activation-setup",
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
        },
        method: "POST",
        body,
        parse: (json: any) => json as IJuniorContractResponse
    };
}

export function postJuniorActivationCode(body: IJuniorActivationCodeRequest): ITypedAPITarget<IJuniorActivationCodeResponse> {
    return {
        url: `/rest/my/software-token/junior/${body.juniorUserNumber}/activation`,
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
        },
        method: "POST",
        body,
        parse: json => json as IJuniorActivationCodeResponse
    };
}

export function getUsersForCustomer(customerId: string): ITypedAPITarget<IProfile[]> {
    return {
        url: "/rest/netbanking/protected/customers/" + customerId + "/users",
        headers: {
            "Accept": "application/vnd.at.sitsolutions.services.ebv.customer.representation.simple.user.collection+json",
        },
        parse: (json: any) => {
            return json as IProfile[];
        }
    };
}

export function getUserByUserNumber(userNumber: string): ITypedAPITarget<IProfile[]> {
    return {
        url: "/rest/netbanking/my/protected/users/" + userNumber,
        headers: {
            "Accept": "application/vnd.at.sitsolutions.services.ebv.user.representation.protected.user+json",
        },
        parse: (json: any) => {
            return json as IProfile[];
        }
    };
}

export function createHouseholdInsurance(payload: {
    addressContactId: string;
    emailContactId: string;
    mobilePhoneContactId: string;
}): ITypedAPITarget<{ url: string }> {
    return {
        url: "rest/products/external/insurances/household",
        headers: {
            "Content-Type": "application/vnd.presto.latest+json",
        },
        method: "POST",
        body: payload,
        parse: (json: any) => {
            return json as { url: string };
        }
    };
}

export function getTradingPreferences(): ITypedAPITarget<IPreferenceList> {
    return {
        url: "preferences/rest/preferences",
        headers: {
            "Accept": "application/vnd.at.sitsolutions.services.pms.preferences.preferences.output+json",
        },
        method: "GET",
        parse: (json: any) => {
            return json as IPreferenceList;
        }
    };
}

export function putTradingPreferences(payload: IPutPreferenceListPayload): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: "preferences/rest/preferences",
        headers: {
            "Content-Type": "application/json",
        },
        method: "PUT",
        body: payload,
        parse: (json: any) => {
            return json as any;
        }
    };
}


// WEBPACK FOOTER //
// ./src/network/NetbankingAPI.ts